<template>
    <modal-ui
        :id="id"
        size="modal-md"
        :title="title">
        <form class="loader" @submit.prevent="submit">
            <loader-ui :is-show="loader" />
            <div class="row mb-3">
                <div class="col-12">
                    <div v-if="subject" class="mb-3">
                        Вы действительно хотите удалить <strong>{{ subject }}</strong>?
                    </div>
                    <div v-if="warning" class="alert alert-warning py-2 small">
                        {{ warning }}
                    </div>
                    <form-group-ui
                        :invalid-feedback="`Укажите причину удаления (от ${minLength} до ${maxLength} символов)`"
                        label="Причина удаления"
                        required>
                        <text-area-ui
                            v-model="reason"
                            :is-invalid="visibleErrors && isReasonError"
                            :maxlength="maxLength"
                            :placeholder="placeholder" />
                    </form-group-ui>
                    <div class="form-text">
                        Запись не удаляется безвозвратно: её можно восстановить в разделе «Корзина».
                    </div>
                </div>
            </div>
            <div class="d-flex justify-content-center">
                <button-ui button-class="btn-danger">
                    Удалить
                </button-ui>
                <button-ui button-class="btn-outline-secondary ms-3" type="button" @click="cancel">
                    Отмена
                </button-ui>
            </div>
        </form>
    </modal-ui>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useModal } from "@/composables/useModal";
import useNotifications from "@/composables/useNotifications";

export const DELETE_REASON_MIN_LENGTH = 3;
export const DELETE_REASON_MAX_LENGTH = 1000;

/**
 * Общая модалка удаления с обязательной причиной.
 * modal.data: { title, subject, warning?, placeholder?, request: (reason) => Promise }
 */
export default defineComponent({
    name: "DeleteReasonModal",
    props: {
        id: {
            type: String,
            default: '',
        },
    },
    setup(props) {
        const modal = useModal(props.id);
        const minLength = DELETE_REASON_MIN_LENGTH;
        const maxLength = DELETE_REASON_MAX_LENGTH;

        const title: string = modal.data.title || 'Удаление записи';
        const subject: string = modal.data.subject || '';
        const warning: string = modal.data.warning || '';
        const placeholder: string = modal.data.placeholder || 'Опишите, почему запись удаляется';
        const request: (reason: string) => Promise<any> = modal.data.request;

        const reason = ref('');
        const visibleErrors = ref(false);
        const loader = ref(false);

        const isReasonError = computed(() => {
            const length = reason.value.trim().length;
            return length < minLength || length > maxLength;
        });

        function submit() {
            visibleErrors.value = true;
            if (isReasonError.value || loader.value) return;

            const value = reason.value.trim();
            loader.value = true;
            request(value).then((response: any) => {
                modal.ok({ reason: value, response });
            }).catch((e: any) => {
                if (!e?.__apiErrorNotified) {
                    useNotifications.error('Ошибка', 'Не удалось удалить запись');
                }
            }).finally(() => {
                loader.value = false;
            });
        }

        function cancel() {
            modal.cancel({});
        }

        return {
            title, subject, warning, placeholder, reason, visibleErrors, loader, isReasonError, minLength, maxLength, submit, cancel,
        };
    },
});
</script>
